import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FieldDataTypeBadge } from '@/components/badges/FieldDataTypeBadge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AppCheckboxField, AppInputField, AppSelectField } from '@/form/FormUI';
import { SelectOptionsEditor } from './SelectOptionsEditor';
import { appEquipmentConfigState, equipmentTypeEditState } from '@/state';
import { useAsyncAtomValue, useLoadableSetAtom } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import logger from '@/util/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { visibleCoreFields } from '@rock/util';
import {
    DimensionsSubTypeSchema,
    type EquipmentType,
    type EquipmentTypeId,
    type InputField,
    InputFieldSchema,
    LengthUnitSchema,
    type SpaceId,
} from '@rock/types';
import { type FC, useCallback, useState } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

const log = logger('EquipmentFieldsTable');

const EquipmentFieldsTable: FC<{ spaceId: SpaceId; typeId: EquipmentTypeId }> = ({ spaceId, typeId }) => {
    const appEquipmentConfig = useAsyncAtomValue(appEquipmentConfigState);
    const coreFields = appEquipmentConfig?.coreFields;
    const [equipmentTypeLoadable, updateEquipmentType] = useLoadableSetAtom(equipmentTypeEditState(typeId));
    const equipmentType = equipmentTypeLoadable.state === 'hasData' ? equipmentTypeLoadable.data : undefined;

    if (!equipmentType) return null;

    const scope = equipmentType.scope ?? 'normal';
    // Only the fields this equipment can actually be asked for: `visibleCoreFields` drops the ones
    // scoped elsewhere and the ones switched off (Equipment → Core Fields), and the split below is
    // presentational — unscoped fields, then the ones this scope earns.
    const offeredCoreFields = visibleCoreFields(coreFields ?? [], { scope, typeId: equipmentType.id });
    const commonCoreFields = offeredCoreFields.filter((f) => !f.equipmentScopes);
    const scopedCoreFields = offeredCoreFields.filter((f) => !!f.equipmentScopes);

    return (
        <div className="space-y-5">
            <h3>Input Fields</h3>
            <div>These are the fields that users can enter data for when setting up this Equipment.</div>
            <div className="space-y-3">
                <h6>Core Fields (common to all Equipment)</h6>
                <FieldsTable fields={commonCoreFields} />
            </div>
            {scopedCoreFields.length > 0 && (
                <div className="space-y-3">
                    <h6>Core Fields (current scope: {scope})</h6>
                    <FieldsTable fields={scopedCoreFields} />
                </div>
            )}
            <div className="space-y-3">
                <h6>Equipment-Specific Fields</h6>
                <ConfigurableFieldsTable
                    spaceId={spaceId}
                    type={equipmentType}
                    updateEquipmentType={updateEquipmentType}
                />
            </div>
        </div>
    );
};

const ConfigurableFieldsTable: FC<{
    spaceId: SpaceId;
    type: EquipmentType;
    updateEquipmentType: (equipmentType: EquipmentType) => Promise<void>;
}> = ({ spaceId, type, updateEquipmentType }) => {
    const [editFieldDataKey, setEditFieldDataKey] = useState<string | null | undefined>(undefined);
    const editingField = type.inputFields?.find((f) => f.dataKey === editFieldDataKey);
    const [loading, setLoading] = useState(false);

    async function saveInputField(field: InputField) {
        if (field.dataType === 'select' && (!field.options || field.options.length === 0)) {
            toast.error('Select fields require at least one option');
            return;
        }
        setLoading(true);
        try {
            if (editFieldDataKey === null) {
                await updateEquipmentType({
                    ...type,
                    inputFields: (type.inputFields ?? []).concat(field),
                });
                toast.success('Created Input Field');
            } else {
                await updateEquipmentType({
                    ...type,
                    inputFields: type.inputFields?.map((f) => (f.dataKey === editFieldDataKey ? field : f)),
                });
                toast.success('Editing Input Field');
            }
            setEditFieldDataKey(undefined);
        } catch (err) {
            log.error('Error Saving Input Field', { err, field });
            toast.error('Error Saving Input Field');
        } finally {
            setLoading(false);
        }
    }

    async function deleteInputField() {
        setLoading(true);
        try {
            await updateEquipmentType({
                ...type,
                inputFields: type.inputFields?.filter((f) => f.dataKey !== editFieldDataKey),
            });
            toast.success('Deleted Input Field');
            setEditFieldDataKey(undefined);
        } catch (err) {
            log.error('Error Deleting Field', { err, editFieldDataKey });
            toast.error('Error Deleting Input Field');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="space-y-2">
            <div className="flex flex-row justify-end">
                <Button
                    onClick={() => setEditFieldDataKey(null)}
                    variant="outline"
                    size="sm"
                    className="cursor-pointer"
                >
                    Add Field
                </Button>
            </div>
            <FieldsTable fields={type.inputFields ?? []} onClick={(f) => setEditFieldDataKey(f.dataKey)} />
            {editFieldDataKey !== undefined && (
                <EditInputFieldForm
                    spaceId={spaceId}
                    typeId={type.id}
                    loading={loading}
                    inputField={editingField}
                    onSave={saveInputField}
                    onDelete={deleteInputField}
                    onCancel={() => setEditFieldDataKey(undefined)}
                />
            )}
        </div>
    );
};

const FieldsTable: FC<{ fields: InputField[]; onClick?: (f: InputField) => void }> = ({ fields, onClick }) => {
    return (
        <div className="px-4 pb-4">
            <Table className="not-prose">
                <TableHeader className="bg-secondary">
                    <TableRow>
                        <TableHead>Data Key</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Data Type</TableHead>
                        <TableHead>Help Text</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {fields.map((f) => (
                        <TableRow
                            key={f.dataKey}
                            onClick={onClick ? () => onClick(f) : undefined}
                            className="cursor-pointer"
                        >
                            <TableCell>{f.dataKey}</TableCell>
                            <TableCell>{f.label}</TableCell>
                            <TableCell>
                                <FieldDataTypeBadge
                                    dataType={f.dataType}
                                    dimensionsSubType={f.dimensionsSubType}
                                    defaultLengthSelection={f.defaultLengthSelection}
                                    options={f.options}
                                />
                            </TableCell>
                            <TableCell>{f.helpText}</TableCell>
                        </TableRow>
                    ))}
                    {fields.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4}>
                                <div>No fields have been setup</div>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

type EditInputFieldProps = {
    spaceId: SpaceId;
    typeId: EquipmentTypeId;
    loading: boolean;
    inputField?: InputField;
    onSave: (inputField: InputField) => void;
    onDelete: () => void;
    onCancel: () => void;
};

const EditInputFieldForm: FC<EditInputFieldProps> = ({
    spaceId,
    typeId,
    loading,
    inputField,
    onSave,
    onDelete,
    onCancel,
}) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const form = useForm<z.input<typeof InputFieldSchema>, unknown, InputField>({
        resolver: zodResolver(InputFieldSchema),
        defaultValues: inputField || {
            dataKey: '',
            label: '',
            dataType: 'text',
            helpText: '',
            dimensionsSubType: undefined,
        },
    });

    const watchedFields = useWatch({ control: form.control });

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    return (
        <Dialog open onOpenChange={onCancel}>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSave, onInvalid)}>
                        <DialogHeader>
                            <DialogTitle>{inputField?.label || 'Create New Input Field'}</DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                            <div className="flex flex-col gap-4 items-stretch p-4">
                                <AppInputField
                                    label="Data Key"
                                    placeholder="Unique identifier (not editable after create)"
                                    control={form.control}
                                    name="dataKey"
                                />
                                <AppInputField
                                    label="Label"
                                    control={form.control}
                                    name={`label`}
                                    placeholder="Label"
                                />
                                <AppSelectField
                                    label="Data Type"
                                    control={form.control}
                                    name="dataType"
                                    options={[
                                        'text',
                                        'number',
                                        'boolean',
                                        'model-number',
                                        'serial-number',
                                        'brand',
                                        'dimensions',
                                        'year',
                                        'month-year',
                                        'date',
                                        'image',
                                        'select',
                                    ]}
                                />
                                {watchedFields.dataType === 'select' && (
                                    <SelectOptionsEditor
                                        form={form}
                                        name="options"
                                        storagePathPrefix={`spaces/${spaceId}/equipment-types/${typeId}/images`}
                                    />
                                )}
                                {watchedFields.dataType === 'dimensions' && (
                                    <AppSelectField
                                        label="Dimensions Sub Type"
                                        control={form.control}
                                        name="dimensionsSubType"
                                        options={Object.values(DimensionsSubTypeSchema.enum)}
                                    />
                                )}
                                {watchedFields.dataType === 'dimensions' &&
                                    watchedFields.dimensionsSubType &&
                                    watchedFields.dimensionsSubType !== 'sqft' && (
                                        <AppSelectField
                                            label="Default Length Selection"
                                            control={form.control}
                                            name="defaultLengthSelection"
                                            options={Object.values(LengthUnitSchema.enum)}
                                        />
                                    )}
                                {watchedFields.dataType === 'dimensions' &&
                                    watchedFields.dimensionsSubType &&
                                    watchedFields.dimensionsSubType !== 'length' &&
                                    watchedFields.dimensionsSubType !== 'sqft' && (
                                        <AppCheckboxField
                                            label="Render Calculated Value"
                                            control={form.control}
                                            name="renderCalculatedValue"
                                            description="When enabled, displays the calculated value (e.g., '20x30 ft' → '600 ft²') instead of the raw dimensions"
                                        />
                                    )}
                                <AppInputField
                                    label="Help Text"
                                    control={form.control}
                                    name={`helpText`}
                                    placeholder="Text to help the user know what to put in this field"
                                />
                            </div>
                        </div>
                        <DialogFooter className="flex flex-row items-center justify-between sm:justify-between">
                            {inputField ? (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    className="float-left"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={loading}
                                >
                                    Delete
                                </Button>
                            ) : (
                                <div />
                            )}
                            <div className="flex flex-row items-center justify-center gap-2">
                                <Button variant="secondary" onClick={onCancel} disabled={loading}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={loading}>
                                    {inputField ? 'Update Field' : 'Create Field'}
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Delete: {inputField?.label}</AlertDialogTitle>
                        <AlertDialogDescription>
                            This cannot be undone. Are you sure you want to delete this field?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer" disabled={loading}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={onDelete} disabled={loading}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
};

export default EquipmentFieldsTable;
